import { useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Group, Quaternion, Vector3 } from 'three'
import { useXR } from '@react-three/xr'
import { Container, Text } from '@react-three/uikit'
import { useDebug, type LoadReport } from '../app/debug'

const _p = new Vector3()
const _q = new Quaternion()
const _fwd = new Vector3()

function Line({ children }: { children: string }) {
  return (
    <Text fontSize={11} color="#d4d4d4">
      {children}
    </Text>
  )
}

function reportLine(id: string, r: LoadReport): string {
  const short = id.slice(0, 4)
  if (r.status === 'ready') {
    const s = r.size ? r.size.map((n) => n.toFixed(2)).join('×') : '?'
    return `${short} ready  native ${s}  max ${r.maxDim?.toFixed(2)}  fit ${r.fit?.toExponential(2)}`
  }
  if (r.status === 'error') return `${short} ERROR ${r.error?.slice(0, 60)}`
  return `${short} ${r.status}`
}

// In-XR diagnostics (gated by ?debug). The only way to read live session/input/model state inside
// an immersive session with no PC attached. Locked to the user's view so it's always readable.
export function DebugHud() {
  const groupRef = useRef<Group>(null)
  const camera = useThree((s) => s.camera)
  const mode = useXR((s) => s.mode)
  const session = useXR((s) => s.session)
  const inputs = useXR((s) => s.inputSourceStates)
  const planes = useXR((s) => s.detectedPlanes)
  const meshes = useXR((s) => s.detectedMeshes)
  const reports = useDebug((s) => s.reports)

  useFrame(() => {
    if (groupRef.current == null) return
    camera.getWorldPosition(_p)
    camera.getWorldQuaternion(_q)
    _fwd.set(0, 0, -1).applyQuaternion(_q)
    groupRef.current.position.copy(_p).addScaledVector(_fwd, 0.8)
    groupRef.current.quaternion.copy(_q)
  })

  const features = session?.enabledFeatures
  const entries = Object.entries(reports)

  return (
    <group ref={groupRef}>
      <Container
        flexDirection="column"
        gap={3}
        width={520}
        padding={12}
        backgroundColor="#0a0a0a"
        borderRadius={8}
        pixelSize={0.0013}
      >
        <Text fontSize={13} color="#22d3ee">
          XR DEBUG
        </Text>
        <Line>{`mode ${mode ?? 'none'}  inputs ${inputs.length}  planes ${planes.length}  meshes ${meshes.length}`}</Line>
        <Line>{`features ${features ? Array.from(features).join(',') : '-'}`}</Line>
        <Line>{`spawned ${entries.length}`}</Line>
        {entries.length === 0 ? (
          <Line>no models spawned</Line>
        ) : (
          entries.map(([id, r]) => (
            <Text
              key={id}
              fontSize={11}
              color={r.status === 'error' ? '#f87171' : r.status === 'ready' ? '#4ade80' : '#fbbf24'}
            >
              {reportLine(id, r)}
            </Text>
          ))
        )}
      </Container>
    </group>
  )
}
